import { SubscriptionIsCanceled, InvoiceNonPayable } from 'src/errors';
import { Coupon, CouponCode, CouponDto } from 'src/models/Coupon';
import { SubscriptionProductId } from 'src/models/SubscriptionProduct';
import { transformUserDto, UserDto } from 'src/models/User';
import { HttpService } from '.';

const getMe = () => HttpService
  .get<UserDto>('/public/users/me?join=subscription&join=subscription.subscriptionProduct&join=subscription.nextSubscriptionProduct&join=coupon&join=coupon.appliesTo')
  .then(transformUserDto);

type ChangeCardResult = {
  requires3DS: boolean;
  clientSecret: string;
  cardSuffix: string | null;
};

const changeCard = (paymentMethodId: string) => HttpService
  .patch<ChangeCardResult>('/public/users/me/card', { paymentMethodId });

const removeCard = () => HttpService
  .delete<{ cardSuffix: string | null }>('/public/users/me/card');

const cancelSubscription = () => HttpService
  .delete('/public/users/me/subscription/cancel')
  .then(getMe);

const undoCancelSubscription = () => HttpService
  .patch('/public/users/me/subscription/reactivate')
  .then(getMe);

const changeSubscription = (id: SubscriptionProductId) => HttpService
  .patch<{ transaction?: { id: string } } | { checkoutUrl: string }>(`/public/users/me/subscription/product/${id}`)
  .then(async (res) => {
    if ('checkoutUrl' in res) {
      return res;
    }
    const user = await getMe();
    return { user, transaction: res.transaction };
  });

const triggerManualPayment = (subscriptionProductId: SubscriptionProductId) => HttpService
  .patch<{ checkoutUrl: string }>('/public/payments/latest-invoice')
  .catch(error => {
    if (error instanceof SubscriptionIsCanceled || error instanceof InvoiceNonPayable) {
      return HttpService.post('/public/payments/product', { subscriptionProductId });
    }
    throw error;
  }).then(async (res) => {
    if (res != null && 'checkoutUrl' in res) {
      return res;
    }
    return await getMe();
  });

const paymentIntent = (subscriptionProductId: SubscriptionProductId) => HttpService
  .post<{ id: string, clientSecret: string }>('/public/payments/payment-intent', { subscriptionProductId });

const confirmPaymentIntent = (paymentIntentId: string) => HttpService
  .post<boolean>('/public/payments/payment-intent-confirm', { paymentIntentId });

type CreateCheckoutSessionParams = {
  couponCode?: string;
  promoCode?: string;
  affiliateId?: string;
};

const checkoutSession = (
  subscriptionProductId: SubscriptionProductId,
  params: CreateCheckoutSessionParams = {},
) => HttpService
  .post<{ url: string }>('/public/payments/checkout-session', { subscriptionProductId, ...params });

const lookupCoupon = (couponCode: string) => HttpService
  .get<CouponDto>(`/public/coupon/${couponCode}`)
  .then(Coupon.fromDto);

const couponEligibility = () => HttpService
  .get<boolean>(`/public/users/me/redeem`);

export type PaymentIssueInfoDto = {
  retryWindowEndsInDays: number | null;
} | null;

const getPaymentIssueInfo = () => HttpService
  .get<PaymentIssueInfoDto>('/public/users/me/payment-issue-info');

const redeemCoupon = (couponCode: CouponCode) => HttpService
  .put(`/public/users/me/redeem/${couponCode}`);

const clearCoupon = () => HttpService
  .put(`/public/users/me/clear-coupon`);

export type RedeemCouponResponse = {
  result: 'promoApplied' | 'subscriptionCreated' | 'periodExtended' | 'checkout';
  url?: string;
}

const redeemCouponV2 = (couponCode: CouponCode) => HttpService
  .put<RedeemCouponResponse>(`/public/users/me/redeemV2/${couponCode}`);

const UsersService = {
  getMe,
  changeCard,
  removeCard,
  cancelSubscription,
  undoCancelSubscription,
  changeSubscription,
  triggerManualPayment,
  paymentIntent,
  confirmPaymentIntent,
  checkoutSession,
  lookupCoupon,
  couponEligibility,
  getPaymentIssueInfo,
  redeemCoupon,
  clearCoupon,
  redeemCouponV2,
};

export default UsersService;
